/**
 * The care pathway, as the patient page reads it.
 *
 * `GET /patient/{tenant}/{id}/state` answers *what was true on that day*; this layer reads
 * `GET /patient/{tenant}/{id}/pathway`, which answers *what happened, and why*. The two are
 * the halves of one page: the state carries the fields the ontology hangs off no act (they describe
 * the person and are shown once), the pathway carries everything it hangs off an act — inside the
 * episode that act belongs to.
 *
 * Nothing here is speciality-specific. Which field opens an episode, which fields describe the act
 * and which describe the person are all `temporal_role` in the ontology, served in `field_roles`.
 *
 * ## Why the time cursor gates here rather than on the server
 *
 * The whole pathway of one dossier is small and fully dated, so a slider move is a local recompute
 * instead of a round-trip — the cursor stays a cursor. `projectPathway` is that recompute, and its
 * rules all answer the same question: *not "is this value missing", but "had this happened yet".*
 */

import dayjs from "dayjs";
import type { PatientSchema } from "@app/types/few-schot-schema";
import { valueLabel } from "@app/utils/value-labels";
import type { ServedGesture } from "@app/utils/resolve-field-value";

export interface PathwayLevel {
  field_key: string;
  label: string;
  top_position: string;
  bottom_position: string;
  region?: string | null;
  n_segments: number;
  n_positions: number;
  contiguous: boolean;
  spans_regions: boolean;
  unparsed?: unknown;
  /** The moment's label already writes this level (« Arthrodèse L4-L5 »), as the server reads it on the
   *  level's axis. */
  named_by_label?: boolean;
}

/** The sentence a value was read from — not always in the moment's own document. */
export interface PathwayPassage {
  verbatim: string;
  source_doc_id: string;
  doc_type?: string | null;
  doc_date?: string | null;
}

export interface PathwayField {
  field_key: string;
  value?: unknown;
  /** `current` = the act itself, `attribute` = a qualifier of it, `antecedent` = a prior state. */
  role?: string | null;
  entity_id?: string | null;
  assertion_id?: string | null;
  /** The sentences the value rests on: its own, or those of the values Mage-X computed it from (an ASA
   *  grade, a BMI). Empty for a clinician's own answer. */
  passages?: PathwayPassage[];
}

export interface PathwayEvent {
  id: string;
  kind: string;
  status: string;
  rank: number;
  is_index: boolean;
  occurred_lo: string;
  occurred_hi: string;
  date_source: string;
  label: string;
  label_assertion_id?: string | null;
  source_doc_id?: string | null;
  doc_type?: string | null;
  fields: PathwayField[];
  levels: PathwayLevel[];
}

export interface PathwayEpisode {
  id: string;
  rank: number;
  concept: string;
  indication_label?: string | null;
  indication_field_key?: string | null;
  indication_entity_id?: string | null;
  decision: string;
  /** `null` unless a fact settles it — today only `reprise`, set once a later episode revised it. */
  outcome: string | null;
  opened_lo: string;
  opened_hi: string;
  closed_lo?: string | null;
  closed_hi?: string | null;
  date_source: string;
  practitioner_id?: string | null;
  practitioner_label?: string | null;
  previous_episode_id?: string | null;
  events: PathwayEvent[];
  /** The clinician's gesture each of its rows shows, by field — decided by the server with the value
   *  the episode carries. Absent from a payload served before the server decided it. */
  gestures?: Record<string, ServedGesture>;
}

export interface PathwayResponse {
  tenant_id: string;
  id_patient: string;
  build_version?: string | null;
  episodes: PathwayEpisode[];
  loose_events: PathwayEvent[];
  /** The clinical UNIVERSE: `field_key` → `temporal_role` for the whole speciality, so the page can
   *  name what is missing as well as what is present. */
  field_roles: Record<string, string>;
  /** The RULE: which of those describe an act rather than the person. Computed by the backend from
   *  the speciality's episode grammar — see `PathwayResponse` there for why it is not
   *  `temporal_role` alone. */
  episode_fields: string[];
  /** The fields the ontology declares a problem list. Outside an episode they are the patient's
   *  problems and treatments, drawn in their own card rather than in the profile. */
  problem_list_fields?: string[];
  /** The episode axes the grammar describes; absent means all of them. A pregnancy declares no
   *  decision and no outcome, and the page must not paint « Opérée » on it. */
  natives?: string[] | null;
}

/** True for a value the record does not carry. `false` and `0` are values, not absences — folding
 *  them into "non renseigné" is how a non-smoker becomes an unknown. */
export function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** One dated point of a score field, as the projection emits it. */
export interface ScorePoint {
  date: string;
  score: number;
}

export function isScoreSeries(value: unknown): value is ScorePoint[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (point) =>
        point !== null &&
        typeof point === "object" &&
        !Array.isArray(point) &&
        typeof (point as { score?: unknown }).score === "number",
    )
  );
}

/** One field of one episode: the values its moments established, in order, with their provenance.
 *  Several entries mean the record stated the field more than once inside the same episode — a
 *  correction or an evolution, and in both cases something to look at. */
export interface EpisodeFieldValue {
  value: unknown;
  event: PathwayEvent;
  entity_id?: string | null;
  assertion_id?: string | null;
  passages: PathwayPassage[];
}

export interface EpisodeField {
  field_key: string;
  role: string;
  values: EpisodeFieldValue[];
  /** The value as it stands at the end of the visible window. */
  latest: unknown;
  /** The record stated this field twice, differently, inside one episode. */
  changed: boolean;
}

/** An episode as it reads AT a date: only the moments that had happened, and only the conclusions
 *  the record had reached. `null` on `decision` / `outcome` / `gesture` is the whole point — it says
 *  "not yet", which is a different answer from "not known" and the one a flat record cannot give. */
export interface ProjectedEpisode {
  episode: PathwayEpisode;
  events: PathwayEvent[];
  indexEvent: PathwayEvent | null;
  decision: string | null;
  outcome: string | null;
  gesture: string | null;
  /** An act the record has, named by nothing: its label is not the act's own value. Drawn as
   *  « Geste non nommé » rather than as the moment's fallback label — a kind word or a diagnosis. */
  actUnnamed: boolean;
  /** The levels drawn beside the act's name: those its name does not already write. */
  levels: PathwayLevel[];
  fields: EpisodeField[];
  openedOn: string;
  /** Set once the next episode has opened: until then this one is the current one. */
  closedOn: string | null;
  /** Its latest moment visible at the date — the « dernières nouvelles » of the current episode. */
  lastContactOn: string | null;
}

/** The episode's latest visible moment and the document that holds it — what a statement about a
 *  field the episode never stated describes: everything the clinician could read of it. */
export function lastMomentOf(
  projected: ProjectedEpisode,
): { date: string; docId: string | null } | null {
  const last = projected.events.reduce<PathwayEvent | null>(
    (latest, event) =>
      latest === null || event.occurred_lo > latest.occurred_lo
        ? event
        : latest,
    null,
  );
  return last
    ? { date: last.occurred_lo, docId: last.source_doc_id ?? null }
    : null;
}

/** The moment kinds that evidence a decision NOT to operate, or not to operate yet. Read off the
 *  event vocabulary rather than declared here twice: `traitement_conservateur` is the treatment
 *  itself, `decision` the meeting that settled it. */
const DECISION_KINDS = new Set(["decision", "traitement_conservateur"]);

function serialize(value: unknown): string {
  return JSON.stringify(value ?? null);
}

/** Does this field describe the ACT, or the person who underwent it?
 *
 *  The answer is served, not inferred. It used to be assembled here from `temporal_role` plus the
 *  indication key, and that was already one patch on a column with holes — a third field then turned
 *  up in the same position: `traitements_non_chirurgicaux` carries an empty role while the episode
 *  grammar READS it to decide whether an episode was operated or managed conservatively. A field
 *  that determines an episode's decision is not a property of the person.
 *
 *  So the grammar answers, on the side where it lives, and this is a set membership. The split it
 *  produces is verifiable: on the 55 demo dossiers carrying two episodes, every field it keeps for
 *  the episode differs between them somewhere between 1 and 52 times, and every field it leaves to
 *  the profile differs zero times.
 */
export function isEpisodeField(episodeFields: ReadonlySet<string>, key: string): boolean {
  return episodeFields.has(key);
}

/** The PROFILE's fields: the clinical universe of the speciality, minus what describes an act,
 *  minus what the practitioner switched off.
 *
 *  Exported because two readers need the same answer: the card that draws them, and the bar that
 *  closes the record in one gesture. A bar sweeping a list assembled apart from the one on screen
 *  would validate in the doctor's name a field they never had in front of them — which is exactly
 *  the rule that bar exists under. */
export function profileFieldKeys(
  pathway: PathwayResponse | null,
  schema: PatientSchema,
  hiddenKeys?: ReadonlySet<string>,
): string[] {
  return patientLevelCards(pathway, schema, hiddenKeys).profile;
}

/** The patient's PROBLEMS and TREATMENTS: the declared problem lists that describe no act, and the
 *  fields filed in the same section as one. Empty for a speciality declaring none, which then has no
 *  such card. */
export function problemFieldKeys(
  pathway: PathwayResponse | null,
  schema: PatientSchema,
  hiddenKeys?: ReadonlySet<string>,
): string[] {
  return patientLevelCards(pathway, schema, hiddenKeys).problems;
}

/** Split the fields that describe no act between the two patient-level cards. A section holding a
 *  problem list follows it whole: the classes, levels and treatments filed beside a list belong to
 *  that history, not to the profile. The sections are read before the practitioner's own hidden
 *  fields, so hiding a list never sends its section back to the profile. */
function patientLevelCards(
  pathway: PathwayResponse | null,
  schema: PatientSchema,
  hiddenKeys?: ReadonlySet<string>,
): { profile: string[]; problems: string[] } {
  const episodeFields = new Set(pathway?.episode_fields ?? []);
  const lists = new Set(
    (pathway?.problem_list_fields ?? []).filter(
      (key) => key in schema && !isEpisodeField(episodeFields, key),
    ),
  );
  const sections = new Set<string>();
  for (const key of lists) {
    const group = schema[key].group;
    if (group) sections.add(group);
  }
  const profile: string[] = [];
  const problems: string[] = [];
  for (const key of Object.keys(pathway?.field_roles ?? {})) {
    if (!(key in schema) || hiddenKeys?.has(key) || isEpisodeField(episodeFields, key)) continue;
    const group = schema[key].group;
    if (lists.has(key) || (group !== undefined && sections.has(group))) problems.push(key);
    else profile.push(key);
  }
  return { profile, problems };
}

/** What an episode established: the fields its moments carried that describe the act. */
function fieldsOfEpisode(
  events: PathwayEvent[],
  roles: Record<string, string>,
  episodeFields: ReadonlySet<string>,
): EpisodeField[] {
  const byKey = new Map<string, EpisodeField>();
  for (const event of events) {
    for (const field of event.fields) {
      if (isBlank(field.value)) continue;
      if (!isEpisodeField(episodeFields, field.field_key)) continue;
      let entry = byKey.get(field.field_key);
      if (!entry) {
        entry = {
          field_key: field.field_key,
          role: roles[field.field_key] ?? "",
          values: [],
          latest: null,
          changed: false,
        };
        byKey.set(field.field_key, entry);
      }
      entry.values.push({
        value: field.value,
        event,
        entity_id: field.entity_id,
        assertion_id: field.assertion_id,
        passages: field.passages ?? [],
      });
    }
  }
  for (const entry of byKey.values()) {
    entry.latest = entry.values[entry.values.length - 1]?.value ?? null;
    entry.changed = new Set(entry.values.map((v) => serialize(v.value))).size > 1;
  }
  return Array.from(byKey.values());
}

/** What names an episode across rebuilds. A gesture rebuilds the dossier's pathway on the server and
 *  every episode comes back under a new id; `(concept, rank)` is what the rebuild keeps. */
export function episodeKey(episode: Pick<PathwayEpisode, "concept" | "rank">): string {
  return `${episode.concept}#${episode.rank}`;
}

/**
 * Read every episode of the dossier AT `at` (ISO day; `null` = today, i.e. everything).
 *
 * Four gates, and each one distinguishes "had not happened yet" from "was never written down":
 *
 * - an episode opened after `at` is not shown at all;
 * - a moment that occurred after `at` is not shown, so its fields are not shown either;
 * - the DECISION appears only once a visible moment evidences it — the intervention for an operated
 *   episode, the meeting or the conservative treatment otherwise. `non_documentee` needs no evidence
 *   and shows immediately: absence is not something that arrives later;
 * - the OUTCOME appears only once the episode closed on or before `at`, and `reprise` additionally
 *   waits for the episode that revised it to exist. An episode is not a revision before it is
 *   revised, and dating that backwards is precisely the error the flat record makes.
 *
 * An episode is closed by the next one opening, never by the time elapsed since its last document.
 */
export function projectPathway(
  pathway: PathwayResponse | null,
  at: string | null,
): ProjectedEpisode[] {
  if (!pathway) return [];
  const bound = at ?? dayjs().format("YYYY-MM-DD");
  const visible = pathway.episodes.filter((episode) => episode.opened_lo <= bound);
  const episodeFields = new Set(pathway.episode_fields ?? []);
  const natives = pathway.natives ?? null;
  const describes = (axis: string) =>
    natives === null || natives.includes(axis);
  const projected = visible.map((episode) => {
    const events = episode.events.filter((event) => event.occurred_lo <= bound);
    const indexEvent = events.find((event) => event.is_index) ?? null;
    const settled =
      episode.decision === "non_documentee" ||
      (episode.decision === "operee"
        ? indexEvent !== null
        : events.some((event) => DECISION_KINDS.has(event.kind)));
    const closed = episode.closed_hi ?? null;
    const followed = visible.some(
      (other) =>
        other.opened_lo > episode.opened_lo && other.opened_lo <= bound,
    );
    const revisedLater = episode.outcome !== "reprise" || followed;
    const outcome = closed !== null && closed <= bound && revisedLater ? episode.outcome : null;
    const named = Boolean(indexEvent?.label_assertion_id);
    // The act's levels, beside its gesture: a past operation's, which a report recalls, are not (PT-160).
    const ofTheAct = (level: PathwayLevel) => pathway.field_roles[level.field_key] !== "antecedent";
    const levels = indexEvent
      ? indexEvent.levels.filter((level) => ofTheAct(level) && !(named && level.named_by_label))
      : events.flatMap((event) => event.levels.filter(ofTheAct));
    return {
      episode,
      events,
      indexEvent,
      decision: settled && describes("decision") ? episode.decision : null,
      outcome: describes("outcome") ? outcome : null,
      gesture: indexEvent?.label_assertion_id ? indexEvent.label : null,
      actUnnamed: indexEvent !== null && !indexEvent.label_assertion_id,
      levels,
      fields: fieldsOfEpisode(events, pathway.field_roles, episodeFields),
      openedOn: episode.opened_lo,
      closedOn: followed ? closed : null,
      lastContactOn: events.reduce<string | null>(
        (latest, event) =>
          latest === null || event.occurred_lo > latest
            ? event.occurred_lo
            : latest,
        null,
      ),
    };
  });
  return projected;
}

/** Ontology group of a field, and where that group sits — read off the schema the page already
 *  loads, so a section renamed in the ontology renames itself here. */
export interface FieldSection {
  name: string;
  emoji?: string;
  sortOrder: number;
  keys: string[];
}

/** Lay a set of field keys out in the ontology's own sections and order. Keys the schema does not
 *  carry are dropped: the schema is where `show_on_patient_page` and the restricted-field rules are
 *  already applied, and a page that re-derived visibility from the pathway would leak past them. */
export function sectionsFor(keys: string[], schema: PatientSchema): FieldSection[] {
  const order = new Map(Object.keys(schema).map((key, index) => [key, index]));
  const sections = new Map<string, FieldSection>();
  for (const key of keys) {
    const def = schema[key];
    if (!def) continue;
    const name = def.group ?? "Autres";
    let section = sections.get(name);
    if (!section) {
      section = { name, emoji: def.group_emoji, sortOrder: def.group_sort_order ?? 10_000, keys: [] };
      sections.set(name, section);
    }
    if (!section.emoji && def.group_emoji) section.emoji = def.group_emoji;
    section.keys.push(key);
  }
  for (const section of sections.values()) {
    section.keys.sort((a, b) => (order.get(a) ?? 10_000) - (order.get(b) ?? 10_000));
  }
  return Array.from(sections.values()).sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Score points inside an episode's window — the trajectory that episode is responsible for, rather
 *  than the patient's whole history replayed under each one. */
export function scoresWithin(
  value: unknown,
  from: string,
  to: string | null,
): ScorePoint[] {
  if (!isScoreSeries(value)) return [];
  return value
    .filter((point) => point.date >= from && (to === null || point.date <= to))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** One entry of a chronology: what happened, when, and a line of detail. */
export interface ChronologyItem {
  id: string;
  title: string;
  date: string;
  note?: string | null;
  /** The moment the episode turns on. */
  emphasis?: boolean;
  /** The document this moment is, when it is one. */
  docId?: string | null;
}

/** The day each document of the pathway was written: every document is one of its moments. */
export function documentDates(
  pathway: PathwayResponse | null,
): Map<string, string> {
  const dates = new Map<string, string>();
  const events = [
    ...(pathway?.episodes ?? []).flatMap((episode) => episode.events),
    ...(pathway?.loose_events ?? []),
  ];
  for (const event of events) {
    if (event.source_doc_id && !dates.has(event.source_doc_id)) {
      dates.set(event.source_doc_id, event.occurred_lo);
    }
  }
  return dates;
}

/** The moments of one episode, as chronology items. Exported so the page can render them in the
 *  right-hand column: the chronology belongs where the eye already looks for dates, and having one
 *  inside the episode block AND the agenda beside it put two timelines on screen at once. */
export function episodeChronology(
  events: PathwayEvent[],
  label: (kind: string) => string,
  schema: PatientSchema,
): ChronologyItem[] {
  return events.map((event) => ({
    id: event.id,
    title: label(event.kind),
    date: event.occurred_lo,
    note: eventLabel(event, schema),
    emphasis: event.is_index,
    docId: event.source_doc_id,
  }));
}

/** What an episode was opened for, as the reader reads it: a term of the field the grammar opens
 *  episodes for, shown in that field's labels. `null` while nothing named it. */
export function indicationLabel(
  episode: PathwayEpisode,
  schema: PatientSchema,
): string | null {
  if (!episode.indication_label) return null;
  return valueLabel(
    schema[episode.indication_field_key ?? ""]?.value_labels,
    episode.indication_label,
  );
}

/** A moment's name, as the reader reads it.
 *
 *  The pathway copies it from one of the moment's own values — its act, or the indication it
 *  discusses — so it is that field's term and reads in that field's labels. A name none of its
 *  values carries (a document's own title) is shown as written. */
export function eventLabel(event: PathwayEvent, schema: PatientSchema): string {
  const field = event.fields.find((candidate) =>
    (Array.isArray(candidate.value)
      ? candidate.value
      : [candidate.value]
    ).includes(event.label),
  );
  return field
    ? valueLabel(schema[field.field_key]?.value_labels, event.label)
    : event.label;
}

/** The act an episode answered with, as the reader reads it — `null` while none is named. */
export function gestureLabel(
  projected: ProjectedEpisode,
  schema: PatientSchema,
): string | null {
  return projected.gesture && projected.indexEvent
    ? eventLabel(projected.indexEvent, schema)
    : projected.gesture;
}
